import { formatLocalDateTime } from '../utils/localDateTime';
import React, { useEffect, useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebaseConfig';
import { serviceRequestDownloadUrl, type ServiceRequestFile } from '../common/serviceRequestFiles';
import DeliveredReportAttachment from './ConsultantReportViewer';

type Message = { id: string; kind: 'client' | 'consultant'; body?: string; author?: string;
    createdAt?: string; timeUnknown?: boolean; status?: string; error?: string; files?: ServiceRequestFile[];
    reports?: Array<{ id: string; title: string }> };

const ServiceConversation: React.FC<{ organizationId: string; requestId: string; refreshKey?: string;
    title?: string; consultantCanSend?: boolean; clientCanSend?: boolean }> = ({ organizationId, requestId, refreshKey,
    title = 'Service conversation', consultantCanSend = false, clientCanSend = false }) => {
    const callable = useMemo(() => httpsCallable(functions, 'manageServiceRequests'), []);
    const [messages, setMessages] = useState<Message[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [draft, setDraft] = useState('');
    const [sending, setSending] = useState(false);
    const load = async () => {
        try {
            const result = await callable({ action: 'conversation', organizationId, requestId });
            setMessages((result.data as { messages?: Message[] }).messages || []);
            setError('');
        } catch (failure: any) {
            setError(failure.message || 'Conversation failed loading.');
        } finally {
            setLoading(false);
        }
    };

    const send = async (event: React.FormEvent) => {
        event.preventDefault();
        const body = draft.trim();
        if (!body || sending)
            return;
        setSending(true);
        setError('');
        try {
            if (clientCanSend)
                await httpsCallable(functions, 'manageServiceRecall')({ action: 'clientReply', organizationId, requestId, body });
            else
                await callable({ action: 'sendConsultantMessage', organizationId, requestId, body });
            setDraft('');
            await load();
        } catch (failure: any) {
            setError(failure.message || 'Message failed sending.');
        } finally {
            setSending(false);
        }
    };
    useEffect(() => {
        setLoading(true);
        void load();
        const timer = window.setInterval(() => { if (document.visibilityState === 'visible') void load(); }, 60000);
        return () => window.clearInterval(timer);
    }, [organizationId, requestId, refreshKey]);

    const openFile = async (file: ServiceRequestFile) => {
        const tab = window.open('', '_blank');
        if (tab)
            tab.opener = null;
        try {
            const url = await serviceRequestDownloadUrl(organizationId, requestId, file);
            if (tab)
                tab.location.href = url;
            else
                window.location.assign(url);
        } catch (failure: any) {
            tab?.close();
            setError(failure.message || 'Evidence download failed.');
        }
    };

    return <section className="mt-4 rounded-xl border border-indigo-200 bg-white p-4 text-sm text-slate-800">
        <div className="flex items-center justify-between gap-3"><h3 className="font-bold text-indigo-950">{title}</h3>
            <button type="button" onClick={() => void load()} className="font-bold text-blue-700 hover:underline">Refresh</button></div>
        {error && <p role="alert" className="mt-3 text-red-700">{error}</p>}
        {loading && <p className="mt-3 text-slate-500">Loading conversation…</p>}
        {!loading && !messages.length && <p className="mt-3 text-slate-500">No messages yet.</p>}
        <div className="mt-3 space-y-3" aria-live="polite">{messages.map(message =>
            <article key={message.id} className={`rounded-xl border p-3 ${message.kind === 'client'
                ? 'border-blue-200 bg-blue-50' : 'border-indigo-200 bg-indigo-50'}`}>
                <div className="flex flex-wrap justify-between gap-2 text-xs font-bold">
                    <span>{message.kind === 'client' ? 'Client' : 'Consultant'} · {message.author || 'Unknown'}</span>
                    {message.timeUnknown ? <span>Earlier note · time unavailable</span>
                        : message.createdAt && <time dateTime={message.createdAt}>{formatLocalDateTime(message.createdAt)}</time>}
                </div>
                {message.body && <p className="mt-2 whitespace-pre-wrap">{message.body}</p>}
                {message.reports?.map(report => <DeliveredReportAttachment key={report.id} report={report} />)}
                {!!message.files?.length && <div className="mt-3 flex flex-wrap gap-2">{message.files.map(file =>
                    <button key={file.path} type="button" onClick={() => void openFile(file)}
                        className="rounded-lg border border-blue-300 bg-white px-3 py-2 font-semibold text-blue-700 hover:bg-blue-50">
                        {file.name}</button>)}</div>}
                {message.status && <p className="mt-2 text-xs font-semibold capitalize text-slate-600">Evidence: {message.status.replaceAll('_', ' ')}</p>}
                {message.error && <p className="mt-2 text-xs text-red-700">{message.error}</p>}
            </article>)}</div>
        {(consultantCanSend || clientCanSend) && <form onSubmit={event => void send(event)} className="mt-5 space-y-3 border-t border-slate-200 pt-4">
            <label className="block font-bold text-slate-700">{clientCanSend ? 'Reply to consultant' : 'Message client'}<textarea rows={3} maxLength={12000}
                value={draft} onChange={event => setDraft(event.target.value)}
                className="mt-2 w-full rounded-xl border border-slate-300 bg-white p-3 font-normal text-slate-900 [color-scheme:light]"
                placeholder={clientCanSend ? 'Reply to your consultant.' : 'Ask for missing information or reply to the client.'} /></label>
            <button type="submit" disabled={sending || !draft.trim()}
                className="rounded-xl bg-blue-700 px-5 py-2.5 font-bold text-white disabled:opacity-40">
                {sending ? 'Sending…' : clientCanSend ? 'Send Reply' : 'Send Message'}</button>
        </form>}
    </section>;
};

export default ServiceConversation;
